import { GetServerSidePropsContext } from 'next';
import {
  FeatureKey,
  ListingsSearchType,
  PropertyType,
  ListingSortOptions,
} from '@gql';

export type Feature = {
  key: FeatureKey;
  displayName: string;
  categoryName: string;
  promoted: boolean;
};

export type Point = { lat: number; lng: number };

export type Geometry = {
  centerPoint: Point;
  northEastPoint: Point;
  southWestPoint: Point;
};

// page size should be conducive to grid layouts with 2, 3, and 5 columns
export const searchPageSize = 30;

// Default US place ID
export const DEFAULT_PLACE_ID = 'ChIJCzYy5IS16lQRQrfeQ5K5Oxw';

// Continental US default geometry — avoids the full US bounding box (which
// includes Alaska/Hawaii) that causes an overly zoomed-out initial view.
export const DEFAULT_US_GEOMETRY: Geometry = {
  centerPoint: { lat: 39.8283, lng: -98.5795 },
  northEastPoint: { lat: 49.3843, lng: -66.949 },
  southWestPoint: { lat: 24.396, lng: -125.0 },
};
export const DEFAULT_US_ZOOM = 4;

function safeInt(value: string | undefined | null, fallback: number): number {
  if (!value) return fallback;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function safeFloat(value: string | undefined | null): number | undefined {
  if (!value) return undefined;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseInitSearchType(
  _value: string | null | undefined
): ListingsSearchType {
  // Always use Map search — Region is no longer used.
  // Legacy URLs with searchType=REGION are treated as Map; the SearchProvider
  // resolves place geometry via PlaceFindById before firing the listing search.
  return ListingsSearchType.Map;
}

function parseInitPropertyTypes(
  value: string | null | undefined
): PropertyType[] {
  if (!value) return [];

  return value
    .split(',')
    .filter((propertyType) =>
      Object.values(PropertyType).includes(propertyType as PropertyType)
    ) as PropertyType[];
}

function parseInitActiveFeatures(
  value: string | null | undefined
): FeatureKey[] {
  if (!value) return [];

  return value
    .split(',')
    .filter((featureKey) =>
      Object.values(FeatureKey).includes(featureKey as FeatureKey)
    ) as FeatureKey[];
}

function parseInitGeometry(
  searchParams: URLSearchParams
): Geometry | undefined {
  const northEastLat = safeFloat(searchParams.get('northEastLat'));
  const northEastLng = safeFloat(searchParams.get('northEastLng'));
  const southWestLat = safeFloat(searchParams.get('southWestLat'));
  const southWestLng = safeFloat(searchParams.get('southWestLng'));
  const centerLat = safeFloat(searchParams.get('centerLat'));
  const centerLng = safeFloat(searchParams.get('centerLng'));

  if (
    northEastLat === undefined ||
    northEastLng === undefined ||
    southWestLat === undefined ||
    southWestLng === undefined ||
    centerLat === undefined ||
    centerLng === undefined
  ) {
    return undefined;
  }

  return {
    centerPoint: { lat: centerLat, lng: centerLng },
    northEastPoint: { lat: northEastLat, lng: northEastLng },
    southWestPoint: { lat: southWestLat, lng: southWestLng },
  };
}

/**
 * Canonical search parameters - the single source of truth for search state.
 * Both SSR and client (SearchProvider) must produce identical query variables,
 * so both use buildSearchQueryVariables() with this canonical form.
 */
export interface SearchQueryParams {
  accountId?: string;
  placeId: string;
  searchType: ListingsSearchType;
  checkInDate: string | null; // YYYY-MM-DD format or null
  checkOutDate: string | null;
  adultCount: number;
  childCount: number;
  infantCount: number;
  petCount: number;
  minNumberOfRooms: number;
  minNumberOfBathrooms: number;
  minNumberOfBeds: number;
  minPriceCents?: number;
  maxPriceCents?: number;
  featureKeys: FeatureKey[];
  propertyTypes: PropertyType[];
  cursorAfter?: string;
  cursorBefore?: string;
  pageSize: number;
  // Coordinates — undefined until geometry is resolved (from URL or PlaceFindById)
  northEastLat?: number;
  northEastLng?: number;
  southWestLat?: number;
  southWestLng?: number;
  centerLat?: number;
  centerLng?: number;
}

export interface SearchInitParams {
  cursorAfter: string | null;
  cursorBefore: string | null;
  initActiveFeatures: FeatureKey[];
  initAdultCount: number;
  initCheckInDate: string | null;
  initCheckOutDate: string | null;
  initChildCount: number;
  initInfantCount: number;
  initMapCoordinates: Geometry | undefined;
  initMapZoom: number | undefined;
  initMinNumberOfBathrooms: number;
  initMinNumberOfBeds: number;
  initMinNumberOfRooms: number;
  initMinPrice: number | null;
  initMaxPrice: number | null;
  initPetCount: number;
  initPropertyTypes: PropertyType[];
  initSearchType: ListingsSearchType;
  pageSize: number | undefined;
  placeId: string;
}

export function getDecodedSearchParams(): URLSearchParams {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }

  const rawSearch = window.location.search;

  const hasEncodedDelimiters =
    rawSearch.includes('%3D') || rawSearch.includes('%26');
  const hasLiteralDelimiters =
    rawSearch.includes('=') || rawSearch.includes('&');

  // Only decode when the entire query string is double-encoded (encoded
  // delimiters present but no literal ones). If both exist, values legitimately
  // contain encoded characters and decoding would corrupt them.
  if (hasEncodedDelimiters && !hasLiteralDelimiters) {
    try {
      return new URLSearchParams(decodeURIComponent(rawSearch));
    } catch {
      return new URLSearchParams(rawSearch);
    }
  }

  return new URLSearchParams(rawSearch);
}

/**
 * Parse App Router URLSearchParams into SearchProvider initialization params.
 */
export function buildSearchInitParams(
  searchParams: URLSearchParams
): SearchInitParams {
  const checkInDate = searchParams.get('checkInDate');
  const checkOutDate = searchParams.get('checkOutDate');
  const urlGeometry = parseInitGeometry(searchParams);
  const resolvedPlaceId = searchParams.get('placeId') || DEFAULT_PLACE_ID;
  const isViewportOnlySearch =
    !searchParams.get('placeId') && urlGeometry !== undefined;

  // When no coordinates are in the URL and we're using the default US place
  // ID, use pre-defined continental US geometry instead of resolving via
  // PlaceFindById (which returns a bounding box that includes Alaska/Hawaii
  // and causes an overly zoomed-out initial view).
  const isDefaultPlace = resolvedPlaceId === DEFAULT_PLACE_ID;
  const geometry =
    urlGeometry ?? (isDefaultPlace ? DEFAULT_US_GEOMETRY : undefined);

  const initSearchType = parseInitSearchType(searchParams.get('searchType'));
  const hasGeometry = Boolean(geometry);

  return {
    cursorAfter: searchParams.get('cursorAfter'),
    cursorBefore: searchParams.get('cursorBefore'),
    initActiveFeatures: parseInitActiveFeatures(
      searchParams.get('activeFeatures')
    ),
    initAdultCount:
      searchParams.get('adultCount') && searchParams.get('adultCount') !== '0'
        ? safeInt(searchParams.get('adultCount'), 1)
        : 1,
    initCheckInDate: checkInDate || null,
    initCheckOutDate: checkOutDate || null,
    initChildCount: safeInt(searchParams.get('childCount'), 0),
    initInfantCount: safeInt(searchParams.get('infantCount'), 0),
    initMapCoordinates: geometry,
    initMapZoom:
      hasGeometry && searchParams.get('zoom')
        ? safeInt(searchParams.get('zoom'), 11)
        : isDefaultPlace && !urlGeometry
          ? DEFAULT_US_ZOOM
          : undefined,
    initMinNumberOfBathrooms: safeInt(
      searchParams.get('minNumberOfBathrooms'),
      0
    ),
    initMinNumberOfBeds: safeInt(searchParams.get('minNumberOfBeds'), 0),
    initMinNumberOfRooms: safeInt(searchParams.get('minNumberOfRooms'), 0),
    initMinPrice: safeFloat(searchParams.get('minPrice')) ?? null,
    initMaxPrice: safeFloat(searchParams.get('maxPrice')) ?? null,
    initPetCount: safeInt(searchParams.get('petCount'), 0),
    initPropertyTypes: parseInitPropertyTypes(
      searchParams.get('propertyTypes')
    ),
    initSearchType,
    pageSize: searchParams.get('pageSize')
      ? Math.min(
          Math.max(safeInt(searchParams.get('pageSize'), searchPageSize), 1),
          searchPageSize
        )
      : undefined,
    placeId: isViewportOnlySearch ? '' : resolvedPlaceId,
  };
}

/**
 * Parse URL query parameters into canonical SearchQueryParams.
 */
export function parseSearchQueryParams(
  query: GetServerSidePropsContext['query'],
  accountId?: string
): SearchQueryParams {
  // Always use Map search type — Region is no longer used
  const searchType = ListingsSearchType.Map;

  return {
    accountId,
    placeId: (query?.placeId as string) || DEFAULT_PLACE_ID,
    searchType,
    checkInDate:
      query?.checkInDate && query.checkInDate !== ''
        ? (query.checkInDate as string)
        : null,
    checkOutDate:
      query?.checkOutDate && query.checkOutDate !== ''
        ? (query.checkOutDate as string)
        : null,
    adultCount:
      query?.adultCount && query.adultCount !== '0'
        ? safeInt(query.adultCount as string, 1)
        : 1,
    childCount: safeInt(query?.childCount as string, 0),
    infantCount: safeInt(query?.infantCount as string, 0),
    petCount: safeInt(query?.petCount as string, 0),
    minNumberOfRooms: safeInt(query?.minNumberOfRooms as string, 0),
    minNumberOfBathrooms: safeInt(query?.minNumberOfBathrooms as string, 0),
    minNumberOfBeds: safeInt(query?.minNumberOfBeds as string, 0),
    minPriceCents: undefined, // Calculated client-side from price * stayLength
    maxPriceCents: undefined,
    featureKeys: query?.activeFeatures
      ? ((query.activeFeatures as string)
          .split(',')
          .filter((f) => f.length > 0) as FeatureKey[])
      : [],
    propertyTypes: query?.propertyTypes
      ? ((query.propertyTypes as string)
          .split(',')
          .filter((p) => p.length > 0) as PropertyType[])
      : [],
    cursorAfter: query?.cursorAfter as string | undefined,
    cursorBefore: query?.cursorBefore as string | undefined,
    pageSize: Math.min(
      Math.max(safeInt(query?.pageSize as string, searchPageSize), 1),
      searchPageSize
    ),
    // Coordinates: parsed from URL when present, undefined otherwise.
    // SearchProvider resolves missing coordinates via PlaceFindById.
    northEastLat: accountId ? 90 : safeFloat(query?.northEastLat as string),
    northEastLng: accountId ? 180 : safeFloat(query?.northEastLng as string),
    southWestLat: accountId ? -90 : safeFloat(query?.southWestLat as string),
    southWestLng: accountId ? -180 : safeFloat(query?.southWestLng as string),
    centerLat: accountId ? 0 : safeFloat(query?.centerLat as string),
    centerLng: accountId ? 0 : safeFloat(query?.centerLng as string),
  };
}

/**
 * Build Apollo query variables from canonical SearchQueryParams.
 * This is the SINGLE SOURCE OF TRUTH for query variable construction.
 */
export function buildSearchQueryVariables(params: SearchQueryParams) {
  const variables = {
    accountId: params.accountId,
    placeId: params.placeId,
    searchType: ListingsSearchType.Map,
    checkInDate: params.checkInDate || undefined,
    checkOutDate: params.checkOutDate || undefined,
    minGuestCapacity: params.adultCount + params.childCount,
    minNumberOfRooms: params.minNumberOfRooms,
    minNumberOfBathrooms: params.minNumberOfBathrooms,
    minNumberOfBeds: params.minNumberOfBeds,
    minPriceCents: params.minPriceCents,
    maxPriceCents: params.maxPriceCents,
    features: params.featureKeys,
    propertyTypes:
      params.propertyTypes.length > 0 ? params.propertyTypes : undefined,
    cursorAfter: params.cursorAfter,
    cursorBefore: params.cursorBefore,
    pageSize: params.pageSize,
    petCount: params.petCount,
    northEastLat: params.northEastLat,
    northEastLng: params.northEastLng,
    southWestLat: params.southWestLat,
    southWestLng: params.southWestLng,
    centerLat: params.centerLat,
    centerLng: params.centerLng,
    sort: ListingSortOptions.Relevance,
  };

  return variables;
}

/** Canonical zoom levels for place types. */
const ZOOM_LEVELS: Record<string, number> = {
  country: 5,
  administrative_area_level_1: 7,
  level_one: 7,
};
const DEFAULT_ZOOM = 11;

export const calculateMapZoom = (placeType: string): number => {
  return ZOOM_LEVELS[placeType] ?? DEFAULT_ZOOM;
};

/**
 * Calculate map zoom from Google Place `rawTypes` array (as returned by PlaceFindById).
 * This is used when resolving geometry client-side for geo-only search.
 */
export const calculateMapZoomFromRawTypes = (rawTypes: string[]): number => {
  for (const type of rawTypes) {
    if (type in ZOOM_LEVELS) {
      return ZOOM_LEVELS[type];
    }
  }
  return DEFAULT_ZOOM;
};
